import { useLayoutEffect, useMemo, useRef } from "react";
import { Widget } from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { hasRichText, plainTextToRichText, sanitizeRichText } from "./richText";
import { motionClass, useWidgetMotion } from "@nexus/widget-kit";

export type TextNoteSize = "auto" | "sm" | "md" | "lg" | "xl";

export interface TextNoteConfig {
  title?: string;
  /** Formatted text (a safe subset of HTML, see `richText`). */
  html?: string;
  /** Plain text, from before formatting was available. Read when `html` is absent. */
  text?: string;
  /** Text size. `auto` fits the text to the space the widget has. */
  size?: TextNoteSize;
  showFrame?: boolean;
  /** `banner`: the title as a label beside the text, on one line (see `Banner`). */
  style?: "standard" | "banner";
}

/**
 * Bold words in a banner read as its key figures (3 Mb/s, a model's name): in the
 * primary ink, a touch larger and in tabular figures, like the tiles beside it.
 */
const BANNER_FIGURES =
  "[&_strong]:font-semibold [&_strong]:text-primary [&_strong]:text-[1.08em] [&_strong]:tabular-nums [&_b]:font-semibold [&_b]:text-primary [&_b]:text-[1.08em] [&_b]:tabular-nums";

export const TEXT_NOTE_SIZES: readonly { value: TextNoteSize; label: string }[] = [
  { value: "auto", label: "Auto" },
  { value: "sm", label: "S" },
  { value: "md", label: "M" },
  { value: "lg", label: "L" },
  { value: "xl", label: "XL" },
];

/**
 * Fixed sizes, in pixels against the 13px body text. They are applied in `em` of
 * the surrounding body size (`--text-note-body-size`), so S / M / L / XL keep
 * their proportion to captions and headings on a wall screen as on a workstation.
 */
const SIZE_PX: Record<Exclude<TextNoteSize, "auto">, number> = { sm: 13, md: 16, lg: 22, xl: 32 };

/** A fixed size as `em` of the body size its container carries. */
export function fixedSizeEm(size: Exclude<TextNoteSize, "auto">): string {
  return `${Math.round((SIZE_PX[size] / AUTO_BODY_PX) * 10000) / 10000}em`;
}

/** The container class carrying the surrounding body size (13px, or the presentation body size). */
const BODY_SIZE_CLASS = "text-[length:var(--text-note-body-size,var(--type-body-size))]";

/** Bounds of the automatic size. */
export const AUTO_MIN_PX = 12;
export const AUTO_MAX_PX = 36;

/** The body text size the automatic caps are designed against (13px, `--type-body-size`). */
export const AUTO_BODY_PX = 13;

/**
 * The largest automatic size for a text of this length, in proportion to the
 * body text around it: a short message may read large (a carousel band, a
 * headline beside KPIs), a note of a sentence or two reads as emphasized body
 * text (≈1.4× body) and never outweighs the figures around it. `bodyPx` is the
 * surrounding body size (`--text-note-body-size`): 13px on a workstation, the
 * presentation body size on a wall, so the note keeps the same proportion to
 * captions and headings in both.
 */
export function autoMaxPx(characters: number, bodyPx = AUTO_BODY_PX): number {
  const ratio = Math.max(0.5, bodyPx / AUTO_BODY_PX);
  const base = characters <= 60 ? AUTO_MAX_PX : characters <= 180 ? 18 : 15;
  return Math.round(Math.max(AUTO_MIN_PX, Math.min(AUTO_MAX_PX * ratio, base * ratio)));
}

export const manifest: WidgetManifest = {
  id: "text-note",
  name: "Text",
  description:
    "A free text written by the dashboard author, with simple formatting: a message, a context note, a reminder.",
  version: "1.0.0",
  dataRequests: {},
};

export const defaultConfig: TextNoteConfig = { size: "auto" };

/**
 * The largest font size (between the auto bounds) at which `content` fits in
 * `box`, found by bisection. `null` when the box has no measurable size yet.
 */
export function fitFontSize(
  box: HTMLElement,
  content: HTMLElement,
  max = AUTO_MAX_PX,
): number | null {
  if (box.clientHeight === 0 || box.clientWidth === 0) return null;
  const fits = (size: number) => {
    content.style.fontSize = `${size}px`;
    return content.scrollHeight <= box.clientHeight && content.scrollWidth <= box.clientWidth;
  };
  let low = AUTO_MIN_PX;
  let high = Math.max(AUTO_MIN_PX, max);
  if (fits(high)) return high;
  if (!fits(low)) return low;
  while (high - low > 0.5) {
    const middle = (low + high) / 2;
    if (fits(middle)) low = middle;
    else high = middle;
  }
  return Math.floor(low);
}

/** Theme roles for the colours an author can give part of the text. */
const COLOR_CLASSES = [
  "[&_[data-color=muted]]:text-muted",
  "[&_[data-color=accent]]:text-interaction-text",
  "[&_[data-color=success]]:text-status-ok-text",
  "[&_[data-color=warning]]:text-status-warn-text",
  "[&_[data-color=critical]]:text-status-error-text",
].join(" ");

/** Readable defaults for the few tags the text may hold. */
const PROSE_CLASSES = [
  "[&_p]:my-0 [&_p+p]:mt-[0.5em]",
  "[&_ul]:my-[0.4em] [&_ul]:list-disc [&_ul]:pl-[1.3em]",
  "[&_ol]:my-[0.4em] [&_ol]:list-decimal [&_ol]:pl-[1.3em]",
  "[&_a]:text-interaction-text [&_a]:underline",
  "[&_strong]:font-semibold",
].join(" ");

export const RICH_TEXT_CLASSES = `${PROSE_CLASSES} ${COLOR_CLASSES}`;

/**
 * Free text: no data, nothing to load. With the automatic size the text takes
 * the largest size that fits the widget's space — a short message reads large
 * in a carousel band or beside KPIs, a longer one gets smaller; a fixed size
 * suits long texts in the grid, where the widget grows with them.
 */
export function Component(props: WidgetProps<TextNoteConfig>) {
  return props.config.style === "banner" ? <Banner {...props} /> : <Note {...props} />;
}

/**
 * A note set as a band above the tiles of a war-room page. The title is a small
 * label in capitals on the left, centred on the text and separated by spacing;
 * the text uses secondary ink, with its bold words as key figures.
 * Its paragraphs, as written, follow each other, left-aligned, wrapping
 * naturally; on a narrow widget the label goes above. An edge in
 * the page accent, flush with the frame like the current page of the side list,
 * ties it to the page. Content-sized, never a widget's minimum height.
 */
function Banner({ config }: WidgetProps<TextNoteConfig>) {
  const title = config.title?.trim() ?? "";
  const html = sanitizeRichText(config.html ?? plainTextToRichText(config.text));
  const markup = useMemo(() => ({ __html: html }), [html]);
  const motion = useWidgetMotion();
  const size = config.size && config.size !== "auto" ? config.size : "md";
  const framed = config.showFrame !== false;
  return (
    <Widget
      title={title}
      hideHeader
      titleInContent
      intrinsicHeight
      // The accent edge stands in for the frame's left border, flush like the side list's.
      className={`min-h-0! ${framed ? "border-l-0!" : ""}`}
      framed={framed}
    >
      <div
        className={`relative flex min-w-0 flex-1 flex-wrap items-center gap-x-5 gap-y-1.5 py-3 pr-5 pl-5 ${BODY_SIZE_CLASS}`}
      >
        <span
          aria-hidden
          className="absolute inset-y-2 left-0 w-1 rounded-r-full bg-(--page-accent,var(--border-strong))"
        />
        {title ? (
          <p className="shrink-0 text-metric-caption font-semibold tracking-[0.08em] text-secondary uppercase">
            {title}
          </p>
        ) : null}
        {hasRichText(html) ? (
          <div
            key={html}
            className={`${motionClass(motion, "nx-stagger")} min-w-0 flex-1 basis-60 break-words leading-snug text-secondary [&>div+div]:mt-[0.5em] ${RICH_TEXT_CLASSES} ${BANNER_FIGURES}`}
            style={{ fontSize: fixedSizeEm(size) }}
            dangerouslySetInnerHTML={markup}
          />
        ) : (
          <p className="text-sm text-muted">Write the text in the widget settings.</p>
        )}
      </div>
    </Widget>
  );
}

function Note({ config }: WidgetProps<TextNoteConfig>) {
  const title = config.title?.trim() ?? "";
  const html = sanitizeRichText(config.html ?? plainTextToRichText(config.text));
  // A stable object: React compares `dangerouslySetInnerHTML` by identity, and a new one on
  // every dashboard re-render would rebuild the text (restarting its entrance, losing a selection).
  const markup = useMemo(() => ({ __html: html }), [html]);
  const size = config.size ?? "auto";
  const auto = size === "auto";
  const boxRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const motion = useWidgetMotion();

  useLayoutEffect(() => {
    const box = boxRef.current;
    const content = contentRef.current;
    if (!auto || !box || !content) return;
    const fit = () => {
      // The box carries the surrounding body size (see its class); the text sets its own.
      const bodyPx = Number.parseFloat(getComputedStyle(box).fontSize) || AUTO_BODY_PX;
      const max = autoMaxPx((content.textContent ?? "").trim().length, bodyPx);
      const fitted = fitFontSize(box, content, max);
      content.style.fontSize = `${fitted ?? SIZE_PX.md}px`;
    };
    fit();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(fit);
    observer.observe(box);
    return () => observer.disconnect();
  }, [auto, html]);

  const body = hasRichText(html) ? (
    <div
      // A new text re-enters line by line, like the figures beside it.
      key={html}
      ref={contentRef}
      className={`${motionClass(motion, "nx-stagger")} break-words leading-snug text-primary ${RICH_TEXT_CLASSES}`}
      style={auto ? undefined : { fontSize: fixedSizeEm(size as Exclude<TextNoteSize, "auto">) }}
      // Sanitized above: an allowlist of formatting tags, no script, style or handler.
      dangerouslySetInnerHTML={markup}
    />
  ) : (
    <p className="text-sm text-muted">Write the text in the widget settings.</p>
  );

  return (
    <Widget title={title} hideHeader={!title} framed={config.showFrame !== false}>
      {/* h-full: the body is a block, so only an explicit height lets Auto use the widget's whole space. */}
      <div
        className={`flex h-full min-h-0 flex-1 py-3 ${config.showFrame === false ? "px-0" : "px-4"}`}
      >
        {auto ? (
          // The text is laid out in a box of its own so that fitting it never changes the widget's height.
          <div
            ref={boxRef}
            className={`relative min-h-16 min-w-0 flex-1 overflow-hidden ${BODY_SIZE_CLASS}`}
          >
            <div className="absolute inset-0 flex flex-col justify-center">{body}</div>
          </div>
        ) : (
          <div className={`flex min-w-0 flex-1 flex-col justify-center ${BODY_SIZE_CLASS}`}>
            {body}
          </div>
        )}
      </div>
    </Widget>
  );
}

const widgetModule: WidgetModule<TextNoteConfig> = { manifest, Component, defaultConfig };
export default widgetModule;
export { TEXT_COLORS, hasRichText, plainTextToRichText, sanitizeRichText } from "./richText";
export type { TextColor } from "./richText";
